import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Color } from 'lib/modelBuilder/entities/Color';
import { Cabinet } from 'lib/modelBuilder/entities/Cabinet';
import { createStructuredSelector } from 'reselect';
import { RootState } from 'containers/PanesPanel/types';
import { makeSelectLastHistoryCabinet } from 'containers/Configurator/selectors/history';
import { restoreCabinet } from 'containers/Configurator/actions/furniture';
import ColorPickerList from 'components/ColorPickerList';
import {
  ALUMINIUM_COLOR,
  BLACK_COLOR,
  BLACK_OPAQUE_COLOR,
  GLASS_COLOR,
  MIRROR_COLOR,
  NATURAL_COLOR,
  PaneMaterial,
  WHITE_COLOR,
  WHITE_OPAQUE_COLOR,
} from 'lib/modelBuilder/constants';
import { updatePlateAction } from '../actions';
import { makeSelectColors } from 'containers/Configurator/selectors/colors';
import { useTranslation } from 'react-i18next';
import { getMaterialColor } from 'lib/modelBuilder/utils/colorHelper';
import { PickerTitleCategory } from '../../../components/PickerTitleCategory';
import useMedia from 'use-media';

interface StateProps {
  colors: Color[];
  lastHistoryCabinet: Cabinet;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  colors: makeSelectColors(),
  lastHistoryCabinet: makeSelectLastHistoryCabinet(),
});

interface Props {
  updateShelvesPlates: boolean;
  updateTopPlate: boolean;
  commonColor: Color | undefined;
  hidePanel: any;
}

const PlateColorPicker = ({
  updateShelvesPlates = true,
  updateTopPlate = false,
  commonColor = undefined,
  hidePanel,
}: Props) => {
  const dispatch = useDispatch();
  const { colors, lastHistoryCabinet } = useSelector(stateSelector);
  const { t } = useTranslation();
  const isMobile = useMedia({ maxWidth: 1023 });

  const [material, setMaterial] = useState('base');

  const filterColor = (color: Color) => {
    if (color.order >= 304 || getMaterialColor(color) === PaneMaterial.Alu)
      return false;
    if (material === 'base')
      return (
        color.reference === WHITE_COLOR ||
        color.reference === BLACK_COLOR ||
        color.reference === NATURAL_COLOR
      );
    if (material === PaneMaterial.MDF)
      return getMaterialColor(color) === PaneMaterial.MDF;
    else if (material === PaneMaterial.Frost)
      return (
        getMaterialColor(color) === PaneMaterial.Frost ||
        getMaterialColor(color) === PaneMaterial.FrostOpaque
      );
    else
      return (
        getMaterialColor(color) === PaneMaterial.Glass ||
        getMaterialColor(color) === PaneMaterial.Mirror
      );
  };

  const colorSelected = (color: Color, preview: boolean) => {
    const materialColor = getMaterialColor(color);
    let glass = false;
    if (material === PaneMaterial.MDF || material === PaneMaterial.Frost) {
      glass = true;
    }
    dispatch(
      updatePlateAction(
        glass,
        materialColor,
        color,
        preview,
        updateTopPlate,
        updateShelvesPlates,
      ),
    );
  };

  const onClickColor = (color: Color) => {
    colorSelected(color, false);
  };

  const onMouseOverColor = (color: Color) => {
    colorSelected(color, true);
  };

  const onDoubleClickColor = () => {
    hidePanel();
  };

  const onLeave = () => {
    // Restore the last history element
    dispatch(restoreCabinet(lastHistoryCabinet));
  };

  return (
    <div className="mx-0 px-1">
      <div className="flex bg-gray-300">
        <PickerTitleCategory
          selected={material === 'base'}
          selectable
          onClick={() => {
            setMaterial('base');
          }}
        >
          <div className="flex items-baseline">
            {t('Standard')}
            <span className="sethidden lg:block text-xs m-1">7mm</span>
          </div>
        </PickerTitleCategory>
        <PickerTitleCategory
          selected={material === PaneMaterial.MDF}
          selectable
          onClick={() => {
            setMaterial(PaneMaterial.MDF);
          }}
        >
          <div className="flex items-baseline">
            {t('MDF')}
            <span className="sethidden lg:block text-xs m-1">3mm</span>
            {' + '}
            {t('Glass')}
            <span className="sethidden lg:block text-xs m-1">6mm</span>
          </div>
        </PickerTitleCategory>
        <PickerTitleCategory
          selected={material === PaneMaterial.Frost}
          selectable
          onClick={() => {
            setMaterial(PaneMaterial.Frost);
          }}
        >
          <div className="flex items-baseline">
            {t('Acrylic')}
            <span className="sethidden lg:block text-xs m-1">3mm</span>
            {' + '}
            {t('Glass')}
            <span className="sethidden lg:block text-xs m-1">6mm</span>
          </div>
        </PickerTitleCategory>
        <PickerTitleCategory
          selected={
            material === PaneMaterial.Glass || material === PaneMaterial.Mirror
          }
          selectable
          onClick={() => {
            setMaterial(PaneMaterial.Glass);
          }}
        >
          {t('Others')}
        </PickerTitleCategory>
      </div>
      {colors && (
        <div onMouseLeave={onLeave}>
          <ColorPickerList
            colors={colors.filter(color => filterColor(color))}
            onClick={onClickColor}
            onDoubleClick={onDoubleClickColor}
            onMouseOver={onMouseOverColor}
            selectedColor={undefined}
            bigColors={
              material !== PaneMaterial.MDF
                ? [
                    WHITE_COLOR,
                    NATURAL_COLOR,
                    BLACK_COLOR,
                    WHITE_OPAQUE_COLOR,
                    BLACK_OPAQUE_COLOR,
                    GLASS_COLOR,
                    MIRROR_COLOR,
                    ALUMINIUM_COLOR,
                  ]
                : undefined
            }
            glass={
              material === PaneMaterial.MDF || material === PaneMaterial.Frost
            }
            dimension={
              material === PaneMaterial.Glass ||
              material === PaneMaterial.Mirror
                ? '(6mm)'
                : undefined
            }
          />
        </div>
      )}
    </div>
  );
};

export default PlateColorPicker;
